import React, { useEffect, useState } from 'react';
import apiService from '../services/api';
import { Structure } from '../types';
import RichTextEditor from '../components/RichTextEditor';
import {
  PaperAirplaneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const htmlIsEmpty = (html: string) =>
  html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length === 0;

const Communication: React.FC = () => {
  const [structures, setStructures] = useState<Structure[]>([]);
  const [loadingStructures, setLoadingStructures] = useState(true);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [allStructures, setAllStructures] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    apiService.getStructures()
      .then(setStructures)
      .catch(() => setError('Impossible de charger les structures.'))
      .finally(() => setLoadingStructures(false));
  }, []);

  const toggleStructure = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const canSend =
    subject.trim().length > 0 &&
    !htmlIsEmpty(body) &&
    (allStructures || selected.size > 0);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(null);
    if (!canSend) {
      setError('Renseignez l’objet, le message et au moins une structure (ou « toutes »).');
      return;
    }
    const target = allStructures
      ? 'tous les utilisateurs'
      : `${selected.size} structure${selected.size > 1 ? 's' : ''}`;
    if (!window.confirm(`Envoyer cette communication à ${target} ?`)) return;

    setSending(true);
    try {
      const res = await apiService.sendBroadcast({
        subject: subject.trim(),
        body,
        allStructures,
        structureIds: allStructures ? undefined : Array.from(selected),
      });
      setSuccess(res.message || `Communication envoyée à ${res.recipients} destinataire(s).`);
      // Réinitialise le formulaire
      setSubject('');
      setBody('');
      setAllStructures(false);
      setSelected(new Set());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Échec de l’envoi de la communication.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-10">
      {/* En-tête */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-primary-100 flex items-center justify-center">
          <EnvelopeIcon className="h-6 w-6 text-primary-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Communication groupée</h1>
          <p className="text-sm text-gray-500">Envoyez un e-mail aux utilisateurs des structures choisies.</p>
        </div>
      </div>

      {success && (
        <div className="flex items-start gap-2.5 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">
          <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSend} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
        {/* Objet */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Objet</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Objet de la communication"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2
              focus:ring-primary-500 focus:border-primary-500 outline-none placeholder-gray-300 transition-shadow"
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
          <RichTextEditor
            value={body}
            onChange={setBody}
            placeholder="Rédigez votre message…"
            minHeight={220}
          />
        </div>

        {/* Destinataires */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Destinataires</label>

          <label className="flex items-center gap-2.5 px-4 py-3 bg-primary-50 border border-primary-100 rounded-xl cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={allStructures}
              onChange={(e) => setAllStructures(e.target.checked)}
              className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-800">Toutes les structures</span>
            <span className="text-xs text-gray-400 ml-auto">tous les utilisateurs actifs</span>
          </label>

          {!allStructures && (
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {loadingStructures ? (
                <div className="px-4 py-6 text-center text-sm text-gray-400">Chargement des structures…</div>
              ) : structures.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-gray-400">Aucune structure disponible.</div>
              ) : (
                structures.map((s) => (
                  <label key={s.id} className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(s.id)}
                      onChange={() => toggleStructure(s.id)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-800">{s.nom}</span>
                    {s.sigle && <span className="text-xs text-gray-400">({s.sigle})</span>}
                  </label>
                ))
              )}
            </div>
          )}
          {!allStructures && selected.size > 0 && (
            <p className="text-xs text-gray-400 mt-2">{selected.size} structure(s) sélectionnée(s).</p>
          )}
        </div>

        {/* Action */}
        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={sending || !canSend}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white
              rounded-xl text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <PaperAirplaneIcon className="h-4 w-4" />
            )}
            {sending ? 'Envoi en cours…' : 'Envoyer la communication'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Communication;
